import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';
import type { Func, Predicate } from '@ricoh/ria.client.core/types/core/function.type';

import { ClientError } from '@ricoh/ria.client.core/errors';

const createRequest = <TData>(create: Func<[], IDBRequest<TData>>) =>
  new Promise<TData>((resolve, reject) => {
    const request = create();

    request.onsuccess = ({ target }) => {
      resolve((target as IDBRequest<TData>).result);
    };

    request.onerror = ({ target }) => {
      reject(
        new ClientError({
          module: 'plugin',
          message: `Failed to execute database request`,
          cause: (target as IDBRequest<TData>).error,
        }),
      );
    };
  });

/** DatabaseStorage API. */
const storageApi = {
  open: async (name: string, version: number, migrate?: Func<[db: IDBDatabase]>): Promise<IDBDatabase> =>
    new Promise<IDBDatabase>((resolve, reject) => {
      const request = globalThis.indexedDB.open(name, version);
      request.onupgradeneeded = ({ target }) => {
        if (!migrate) {
          return;
        }

        const database = (target as IDBOpenDBRequest).result;
        migrate(database);
      };

      request.onsuccess = ({ target }) => {
        const database = (target as IDBOpenDBRequest).result;

        resolve(database);
      };

      request.onerror = ({ target }) => {
        reject(
          new ClientError({
            module: 'plugin',
            message: `Failed to open database`,
            cause: (target as IDBOpenDBRequest).error,
          }),
        );
      };
    }),

  readAll: async <TData>(database: IDBDatabase, store: string): Promise<TData[]> =>
    createRequest(() => {
      const transaction = database.transaction(store, 'readonly');
      const objectStore = transaction.objectStore(store);

      return objectStore.getAll();
    }),

  read: async <TData>(database: IDBDatabase, store: string, key: string): Promise<Nullable<TData>> =>
    createRequest(() => {
      const transaction = database.transaction(store, 'readonly');
      const objectStore = transaction.objectStore(store);

      return objectStore.get(key);
    }),

  readBy: <TData>(database: IDBDatabase, store: string, predicate: Predicate<TData>): Promise<TData[]> =>
    new Promise<TData[]>((resolve, reject) => {
      const transaction = database.transaction(store, 'readonly');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.openCursor();

      const results: TData[] = [];

      request.onsuccess = ({ target }) => {
        const cursor = (target as IDBRequest<IDBCursorWithValue>).result;

        if (cursor) {
          const record = cursor.value;
          if (predicate(record)) {
            results.push(record);
          }

          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = ({ target }) => {
        reject(
          new ClientError({
            module: 'plugin',
            message: `Failed to execute database request`,
            cause: (target as IDBRequest<IDBCursorWithValue>).error,
          }),
        );
      };
    }),

  create: async <TData>(database: IDBDatabase, store: string, data: TData) => {
    await createRequest(() => {
      const transaction = database.transaction(store, 'readwrite');
      const objectStore = transaction.objectStore(store);

      return objectStore.add(data);
    });
  },

  update: async <TData>(database: IDBDatabase, store: string, data: Partial<TData>) => {
    await createRequest(() => {
      const transaction = database.transaction(store, 'readwrite');
      const objectStore = transaction.objectStore(store);

      return objectStore.put(data);
    });
  },

  updateBy: <TData>(database: IDBDatabase, store: string, predicate: Predicate<TData>, data: Partial<TData>) =>
    new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(store, 'readwrite');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.openCursor();

      request.onsuccess = ({ target }) => {
        const cursor = (target as IDBRequest<IDBCursorWithValue>).result;

        if (cursor) {
          const record = cursor.value;
          if (predicate(record)) {
            cursor.update({ ...record, ...data });
          }

          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = ({ target }) => {
        reject(
          new ClientError({
            module: 'plugin',
            message: `Failed to execute database request`,
            cause: (target as IDBRequest<IDBCursorWithValue>).error,
          }),
        );
      };
    }),

  delete: async (database: IDBDatabase, store: string, key: string) => {
    await createRequest(() => {
      const transaction = database.transaction(store, 'readwrite');
      const objectStore = transaction.objectStore(store);

      return objectStore.delete(key);
    });
  },

  deleteBy: <TData>(database: IDBDatabase, store: string, predicate: Predicate<TData>) =>
    new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(store, 'readwrite');
      const objectStore = transaction.objectStore(store);
      const request = objectStore.openCursor();

      request.onsuccess = ({ target }) => {
        const cursor = (target as IDBRequest<IDBCursorWithValue>).result;

        if (cursor) {
          const record = cursor.value;
          if (predicate(record)) {
            cursor.delete();
          }

          cursor.continue();
        } else {
          resolve();
        }
      };

      request.onerror = ({ target }) => {
        reject(
          new ClientError({
            module: 'plugin',
            message: `Failed to execute database request`,
            cause: (target as IDBRequest<IDBCursorWithValue>).error,
          }),
        );
      };
    }),
};

export default storageApi;
